import React, { useEffect, useState } from "react";
import axiosInstance from "../../api_calls/axiosInstance";
import Pagination from "components/Pagination/Pagination.js";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import ProfileCard from "./ProfileCard";
import { useAuth } from "../../../src/context/AuthContext";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import Configs from "../../utils/Configs";
import StorageKeys from "../../constants/storageKeys";

const useStyles = makeStyles(styles);

export default function SearchResultsPage() {
  const classes = useStyles();
  const location = useLocation(); // watch route changes

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});

  const { user } = useAuth();

  useEffect(() => {
    // Reset to first page on new filters / URL change
    setCurrentPage(1);
  }, [location]);

  useEffect(() => {
    const fetchFilteredProfiles = async () => {
      if (!user) return;
      setLoading(true);

      try {
        const stored = localStorage.getItem(StorageKeys.SEARCH_FILTERS);
        if (!stored) {
          setProfiles([]);
          setLoading(false);
          return;
        }

        const { filters } = JSON.parse(stored);
        setActiveFilters(filters); // Save filters for pills display

        const params = {
          user_id: user.user_id,
          filters: JSON.stringify(filters),
          page: currentPage,
        };

        console.log("🔍 Sending request with params:", params);

        const response = await axiosInstance.get(Configs.filterUsersEp, { params });

        console.log("📦 Received response:", response.data.data.circles);

        if (response.data && response.data.data.circles) {
          setProfiles(response.data.data.circles);
          setTotalPages(response.data.data.total_pages || 1);
        } else {
          setProfiles([]);
        }
      } catch (error) {
        console.error("❌ Error fetching search results:", error);
        setProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredProfiles();
  }, [currentPage, user, location]); // re-run on page, user, or URL change

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderFilterPills = () => {
    return Object.entries(activeFilters).flatMap(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return [];

      const values = Array.isArray(value) ? value : [value];
      return values.map((v, i) => (
        <Chip
          key={`${key}-${v}-${i}`}
          label={`${key.replaceAll("_", " ")}: ${v}`}
          color="primary"
          variant="outlined"
          sx={{ margin: "4px" }}
        />
      ));
    });
  };

  return (
    <>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "20px",
          fontFamily: "Calibri, sans-serif",
        }}
      >
        Search Results
      </Typography>
      <br />

      {/* Filter Pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", marginBottom: 2 }}>
        {renderFilterPills()}
      </Box>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <CircularProgress color="primary" />
        </div>
      ) : (
        <GridContainer>
          {profiles.length > 0 ? (
            profiles.map((profile, index) => (
              <GridItem key={index} xs={12} sm={12} md={3}>
                <ProfileCard profile={profile} />
              </GridItem>
            ))
          ) : (
            <GridItem xs={12}>
              <Typography>No profiles matched your search.</Typography>
            </GridItem>
          )}
        </GridContainer>
      )}

      <GridContainer justify="center">
        <Pagination
          pages={[
            {
              text: "PREV",
              onClick: () => handlePageChange(currentPage - 1),
              disabled: currentPage === 1,
            },
            ...Array.from({ length: totalPages }, (_, i) => ({
              text: i + 1,
              active: i + 1 === currentPage,
              onClick: () => handlePageChange(i + 1),
            })),
            {
              text: "NEXT",
              onClick: () => handlePageChange(currentPage + 1),
              disabled: currentPage === totalPages,
            },
          ]}
        />
      </GridContainer>
    </>
  );
}
